/**
 * advanceDebt — what one policy year's premium advance owes, and what has been paid against it.
 *
 * THE OWNER'S RULING [D-B46]: *"when a driver pays part of the premium, the interest is only
 * calculated on the remaining amount advanced by Primelane. I thought this has always been the
 * case."* It always had been in the ledger — `get_contract_insights` and `calculate_overdue_interest`
 * have charged on `amount − deposits` throughout — and the Insurance tab was the surface stating it
 * otherwise. So for a premium **A**, a rate **r** and deposits **D**, the amount advanced is
 * `A − D`, the interest is `(A − D)·r/100`, and the total repayable is `(A − D)(1 + r/100)`.
 *
 * AND THE DEPOSIT IS NOT ALSO A REPAYMENT. That is the half of the change that is easy to drop and
 * cannot be: `D` has already come off the debt, so counting it again in the numerator credits the
 * driver twice. Against a net denominator it reads over 100 per cent repaid on three of the seven
 * live part-deposit policies — 256, 127 and 311 per cent, measured — and the tab's own comment used
 * to defer the whole question rather than move one side of the ratio alone. Both sides move here,
 * together, once.
 *
 * ── THE ONE CARVE-OUT, AND WHY IT IS NOT AN EXCEPTION TO THE RULE ──
 * A premium the driver paid IN FULL is on a different instrument [D-B45]: nothing is advanced, no
 * interest arises, nothing is owed, and `agreement_create` publishes an Insurance Premium Payment
 * Record rather than an Advance. Net of a deposit that covers the whole premium is nil, so the net
 * rule read literally would print "Total repayable GH₵0.00" over a "Repaid GH₵3,977.00" — two
 * figures contradicting each other in one row of cards, on a year where nothing is wrong. Taken
 * GROSS, both sides carry the same premium and the percentage falls out at 100 arithmetically.
 * Contract 89 carries both shapes on one tab, which is why this cannot be a per-contract answer.
 * The driver app reached the same carve-out first; this file is that convention, adopted whole.
 *
 * PURE, AND FREE OF THE COMPONENT GRAPH, so vitest can hold it under node. It imports `instrumentFor`
 * and nothing else — the same predicate the board, the setup canvas and the server all read, so the
 * instrument this file branches on can never be a second opinion about which document a year is on.
 */

import { instrumentFor } from '@/lib/agreements/instrument';

/**
 * One `insurance_loans` row as `get_contract_insights` hands it back.
 *
 * SNAKE_CASE AND STRUCTURAL, so the tab passes `insurance.loan` straight in. A mapping step between
 * the payload and this input would be a second place for a key to be renamed and silently read as
 * zero — and every field here is a money figure whose absence must not look like a nought.
 */
export interface AdvanceDebtLoan {
    /** `insurance_loans.amount` — the PREMIUM for the year, never the amount advanced. */
    amount: number;
    interest_rate: number | null;
    /** Σ of the transactions behind `insurance_loan_deposits` — what the driver paid towards it. */
    deposit_total: number;
    /** Σ of `insurance_loan_repayments`. Repayments ONLY — the deposits are the key above. */
    repayments_total: number;
    /** What `calculate_repayment_allocations` has drawn off the contract's weekly payments. */
    allocated_total: number;
    /**
     * `insurance_loan_instalments`, for its LENGTH alone — the instrument's second limb.
     *
     * NULL AND NOT `[]` WHERE THERE ARE NONE, and the distinction is why this is read rather than a
     * count: `get_contract_insights` builds it with `json_agg(...)`, which returns SQL NULL over an
     * empty set (`20260823010000…sql:1111`). A null is therefore a COUNTED zero.
     */
    instalments: readonly unknown[] | null;
}

export interface AdvanceDebt {
    /** Which instrument the year is on [D-B45] — the carve-out above turns on this and nothing else. */
    paidInFull: boolean;
    /** The Amount advanced: `A − D`, or the premium itself on a year paid in full. */
    principal: number;
    /** Interest on the amount advanced. A KNOWN ZERO where nothing was advanced. */
    interest: number;
    /** The Total repayable — `principal + interest`, which is `(A − D)(1 + r/100)` on an advance. */
    total: number;
    /** Repayments and allocations, plus the deposit on a year paid in full and nowhere else. */
    paid: number;
    /** Of the total, capped at 100. Nought where there is nothing to repay. */
    percent: number;
    /**
     * Settled, within the floating-point threshold interest arithmetic needs.
     *
     * `total > 0` IS PART OF IT, AND IT IS THE GUARD THAT KEEPS A POLICY WITH NO LOAN OUT. Every
     * figure here is nought on such a year, and `0 >= 0` would paint it as an advance discharged.
     * The one shape it also catches — an advance whose deposits cover the premium while an
     * instalment row keeps it off the Record — exists on neither tier (0 rows in production and 0
     * in staging, `deposits >= amount − 0.005 AND instalments > 0`).
     */
    paidOff: boolean;
}

const NOTHING: AdvanceDebt = {
    paidInFull: false,
    principal: 0,
    interest: 0,
    total: 0,
    paid: 0,
    percent: 0,
    paidOff: false,
};

/** The floating-point threshold the tab has always settled a loan at: 99.9 per cent of the total. */
const SETTLED = 0.999;

/**
 * DID THE DRIVER PAY THIS PREMIUM IN FULL [D-B45] — the server's own two limbs, over this payload.
 *
 * `instalments` IS NULL AND NOT `[]` WHERE THERE ARE NONE, and that distinction is the whole reason
 * this wrapper exists rather than a spread: a null here is a COUNTED zero, not an unread one, and
 * mapping it to `null` would fall to the advance on exactly the rows this predicate is for.
 */
export function premiumPaidInFull(loan: AdvanceDebtLoan | null | undefined): boolean {
    if (!loan) return false;
    return (
        instrumentFor({
            amount: loan.amount,
            depositTotal: loan.deposit_total ?? 0,
            instalmentCount: loan.instalments?.length ?? 0,
        }) === 'PAYMENT_RECORD'
    );
}

/**
 * The policy year's money, in one derivation.
 *
 * THE TOTAL IS `principal + interest` AND NOT A SECOND SPELLING OF IT. The two are the rows the
 * summary cards print beside it — "Advance" and "Interest" — and their description claims the third
 * card is those two added. Deriving the total any other way lets the claim drift by a rounding.
 */
export function advanceDebt(loan: AdvanceDebtLoan | null | undefined): AdvanceDebt {
    if (!loan) return NOTHING;

    const paidInFull = premiumPaidInFull(loan);
    // WHAT PRIMELANE PUT IN. Not clamped, because the ledger's own reading is not: the interest in
    // `get_contract_insights.total_value` is charged on this same unguarded difference, and a
    // client that clamped where the server does not would state a different debt from the tiles
    // above it. Nothing on either tier is over-deposited with a schedule (see `paidOff`).
    const net = loan.amount - (loan.deposit_total || 0);
    const principal = paidInFull ? loan.amount : net;
    // NOUGHT ON A RECORD AS A FIGURE RATHER THAN AS A ROUNDING. Net is nil there by construction,
    // so this is the same answer the unbranched product gives on every real row — but a premium
    // settled to the last pesewa leaves a fraction of a cedi of "net", and interest on a year
    // nobody borrowed for is not a small number, it is not a number.
    const interest = paidInFull ? 0 : (net * (loan.interest_rate || 0)) / 100;
    const total = principal + interest;

    // THE DEPOSIT JOINS THE NUMERATOR ONLY WHERE IT IS ALSO IN THE DENOMINATOR — the paid-in-full
    // branch, where the premium is on both sides. On an advance it has already reduced the debt.
    const paid =
        (loan.repayments_total || 0) +
        (loan.allocated_total || 0) +
        (paidInFull ? loan.deposit_total || 0 : 0);

    return {
        paidInFull,
        principal,
        interest,
        total,
        paid,
        percent: total > 0 ? Math.min(100, (paid / total) * 100) : 0,
        paidOff: total > 0 && paid >= total * SETTLED,
    };
}

/** The four money figures the row of cards above the policy list prints. */
export interface AdvanceDebtTotals {
    /** Σ of the years' Amount advanced. */
    principal: number;
    /** Σ of the interest charged on them. */
    interest: number;
    /** Σ of the years' Total repayable. */
    total: number;
    /** Σ of what has been paid against them — the Repaid card. */
    paid: number;
}

/**
 * The same figures for every policy year at once — the row of cards above the policy list.
 *
 * ADDING IS THE WHOLE OF IT, AND THAT IS THE POINT [D-B58]. The Repaid card used to be spelled a
 * second time at the call site, over `repayments_total` and the paid-in-full deposit, so it left
 * out what the weekly payment had been credited to the advance while the policy card beneath it
 * counted that money — two repaid figures on one tab, the headline the smaller. A card that is
 * arithmetic over the same derivation cannot drift from the cards it totals. A second spelling of
 * the formula always can, and this one did for as long as both existed.
 */
export function advanceDebtTotals(debts: readonly AdvanceDebt[]): AdvanceDebtTotals {
    return debts.reduce<AdvanceDebtTotals>(
        (sum, debt) => ({
            principal: sum.principal + debt.principal,
            interest: sum.interest + debt.interest,
            total: sum.total + debt.total,
            paid: sum.paid + debt.paid,
        }),
        { principal: 0, interest: 0, total: 0, paid: 0 },
    );
}
